import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttachmentDropzone } from '@/features/DataChangeRequest/Attachments/AttachmentDropzone/AttachmentDropzone';
import { AttachmentGrid } from '@/features/DataChangeRequest/Attachments/AttachmentGrid/AttachmentGrid';

import type { AddFilesResult } from '../../_hooks/useAttachmentUpload';
import type { DcrAttachment } from '../../_types/attachment';
import { ComplianceConfirmation } from '../ComplianceConfirmation/ComplianceConfirmation';
import { ReviewModalNotes } from '../ReviewModalNotes/ReviewModalNotes';

import { reviewModalModalDialogActionsStyles as sx } from './ReviewModalDialogActions.styles';

type Props = {
	onBack: () => void;
	onClose: () => void;
	attachments: DcrAttachment[];
	addFiles: (files: File[]) => AddFilesResult;
	removeAttachment: (id: string) => void;
};

export const ReviewModalDialogActions = ({
	onBack,
	onClose,
	attachments,
	addFiles,
	removeAttachment
}: Props) => {
	const { t } = useTranslation();
	const { control } = useFormContext();

	const isThirdPartyDataConfirmed = useWatch({
		control,
		name: 'isThirdPartyDataConfirmed'
	});

	const isUploading = attachments.some(att => att.status === 'uploading');

	return (
		<Box sx={sx.root}>
			<AttachmentDropzone attachments={attachments} addFiles={addFiles} />
			<AttachmentGrid
				attachments={attachments}
				removeAttachment={removeAttachment}
				size='large'
			/>

			<ReviewModalNotes control={control} expandOnFocus />

			<Divider sx={sx.divider} />

			<ComplianceConfirmation control={control} />

			<Divider sx={sx.divider} />

			<Box sx={sx.actions}>
				<Box sx={sx.backButtonContainer}>
					<Button
						variant='text'
						onClick={onBack}
						startIcon={<NavigateBeforeIcon fontSize='small' />}
					>
						{t('Back')}
					</Button>
				</Box>

				<Button variant='text' onClick={onClose}>
					{t('Cancel')}
				</Button>

				<Button
					type='submit'
					variant='contained'
					disabled={!isThirdPartyDataConfirmed || isUploading}
					sx={sx.submitBtn}
				>
					{t('Submit')}
				</Button>
			</Box>
		</Box>
	);
};
